import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateLocaleFor } from "@/utils/dateLocale";

import { listPriceAt } from "@/lib/discountUtils";

interface Purchase {
  id: string;
  mystery_title: string;
  created_at: string;
  amount: number;
  conversation_id: string;
}

const BillingSettings = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchaseHistory();
  }, [user]);

  const fetchPurchaseHistory = async () => {
    if (!user) return;

    try {
      // Fetch paid conversations (purchases)
      const { data, error } = await supabase
        .from('conversations')
        .select('id, title, created_at, is_paid')
        .eq('user_id', user.id)
        .eq('is_paid', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map to purchase format
      const purchaseData: Purchase[] = (data || []).map((conv) => ({
        id: conv.id,
        mystery_title: conv.title || t('accountSettings.billing.fallbackTitle'),
        created_at: conv.created_at,
        amount: listPriceAt(conv.created_at), // Shown as the regular price in force when it was bought, not the amount charged: sales and discounts do not change this history (Jonathan, 2026-10-14)
        conversation_id: conv.id
      }));

      setPurchases(purchaseData);
    } catch (error) {
      console.error('Error fetching purchase history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMystery = (conversationId: string) => {
    window.location.href = `/mystery/${conversationId}`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("accountSettings.billing.title")}</CardTitle>
          <CardDescription>{t("accountSettings.billing.loading")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("accountSettings.billing.title")}</CardTitle>
        <CardDescription>
          {t("accountSettings.billing.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {purchases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {t("accountSettings.billing.empty")}
            </p>
            <Button asChild>
              <a href="/mystery/create">{t("accountSettings.billing.createFirst")}</a>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="space-y-1 mb-4 md:mb-0">
                  <h3 className="font-semibold">{purchase.mystery_title}</h3>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 text-sm text-muted-foreground">
                    <span>{format(new Date(purchase.created_at), 'PP', { locale: dateLocaleFor(i18n.language) })}</span>
                    <span className="hidden md:inline">•</span>
                    <span className="font-medium text-foreground">
                      {new Intl.NumberFormat(i18n.language, { style: "currency", currency: "USD", currencyDisplay: "code" }).format(purchase.amount)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewMystery(purchase.conversation_id)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t("accountSettings.billing.viewPackage")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {purchases.length > 0 && (
          <div className="mt-8 pt-6 border-t">
            <h4 className="font-semibold mb-2">{t("accountSettings.billing.helpTitle")}</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {t("accountSettings.billing.helpBody")}
            </p>
            <Button variant="outline" asChild>
              <a href="/support">{t("accountSettings.billing.contactSupport")}</a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingSettings;
